import React, { useState, useRef, useEffect, useContext } from "react";
import { IonCol, IonInput, IonItem, IonLabel, IonRow } from "@ionic/react";
import { isWindows } from "../../../utilities/platform/Platform";
import { isPlatform } from "@ionic/core";
import { Label } from "reactstrap";
import { Address, Barn, Trainer } from "../../../models";
import { formatSingleTrainer, formattedOption, getTrainersCreatedByUserOrInCurrentBarn } from "../../../utilities/trainer/SelectOrCreateTrainerUtils";
import { PersonContext } from "../../../context/PersonContext";
import { CreateAddressInput, CreateTrainerInput, UpdateAddressInput, UpdateTrainerInput } from "../../../API";
import { createTrainer, updateTrainer } from "../../../utilities/trainer/Trainer";
import SelectOrCreateTrainer from "../../Trainer/SelectOrCreateTrainer";
import RequiredInputIndicator from "../../Forms/RequiredInputIndicator";
import SelectState from "../../Address/SelectState";
import { FormFieldsInlineSkeleton } from "../../Skeletons/EntrySectionSkeletons";
import { formatShortAddress } from "../../../utilities/address/FormatAddress";
import { createAddress, updateAddress } from "../../../utilities/address/Address";
import MembershipUI from "./MembershipUI";

interface _Props {
    requiredFields?: string[]
    barn?: Barn | null;
    selectedTrainer?: Trainer | null;
    onSelectTrainer?: (trainer?: Trainer, areAllRequiredFieldsComplete?: Boolean) => void;
    onSavingStateChange?: (isSaving: boolean) => void;
    onFieldChange?: (trainer?: Trainer, areAllRequiredFieldsComplete?: Boolean) => void;
    onRegisterSave?: (fn: () => Promise<void> | void) => void;
}

interface TrainerFormState {
    name: string
    city: string
    state: string
}

const SelectTrainerSection: React.FC<_Props> = ({ requiredFields, barn, selectedTrainer, onSelectTrainer, onSavingStateChange, onFieldChange, onRegisterSave }) => {
    const user = useContext(PersonContext);
    const [isLoading, setIsLoading] = useState(false);
    
    // Add ref for click outside detection
    const trainerComponentRef = useRef<HTMLIonRowElement>(null);

    // Add ref to track the current trainer state for the click outside handler
    const selectedTrainerRef = useRef<Trainer | null | undefined>(selectedTrainer);
    
    // Ref to track the latest formState for use in save function (avoids stale closure issues)
    const formStateRef = useRef<TrainerFormState>({
        name: "",
        city: "",
        state: ""
    });
    // Track the last trainer name we tried to save to prevent duplicate saves
    const lastSavedTrainerNameRef = useRef<string>("");

    // selectedTrainerState triggers re-renders and allows us to bind data to the UI
    const [selectedTrainerState, setSelectedTrainerState] = useState<Trainer | null | undefined>();
    const [formState, setFormState] = useState<TrainerFormState>({
        name: "",
        city: "",
        state: ""
    });

    const [userTrainerOptions, setUserTrainerOptions] = useState<formattedOption[] | null | undefined>();
    const [barnTrainerOptions, setBarnTrainerOptions] = useState<formattedOption[] | null | undefined>();

    // Load inital User and Barn options to populate the dropdown
    useEffect(() => {
        const getData = async () => {
            setIsLoading(true);
            const result = await getTrainersCreatedByUserOrInCurrentBarn(user, barn, userTrainerOptions || undefined);

            const createdByOptions = result.createdBy;
            const barnOptions = result.barn;

            setUserTrainerOptions(createdByOptions);
            setBarnTrainerOptions(barnOptions);
            setIsLoading(false);
        };

        getData();
    }, [barn]);

    // Check for a pre-saved Trainer from the parent component (ex: already completed Entry)
    useEffect(() => {
        if (selectedTrainer) {
            // If the parent component DOES pass in a pre-selected Trainer, then set all values
            setSelectedTrainerState(selectedTrainer);
            const trainerCity = getCityFromTrainer(selectedTrainer);
            const trainerState = getStateFromTrainer(selectedTrainer);
            const currentFormState = {
                name: selectedTrainer.name || "",
                city: trainerCity || "",
                state: trainerState || ""
            }
            setFormState(currentFormState);
            formStateRef.current = currentFormState; // Update ref immediately
            // Evaluate if this pre-selected Trainer has all required fields in order to be considered "Complete"
            const isSectionValid = checkIfSectionIsValid(currentFormState);
            if (onFieldChange) onFieldChange(selectedTrainer, isSectionValid);
        } else {
            // If the parent component does NOT pass in a pre-selected Trainer, then clear all values
            setSelectedTrainerState(undefined);
            const emptyFormState = {
                name: "",
                city: "",
                state: ""
            };
            setFormState(emptyFormState);
            formStateRef.current = emptyFormState; // Update ref immediately
        }
    }, [selectedTrainer]);

    // Keep selectedTrainerRef updated
    useEffect(() => {
        selectedTrainerRef.current = selectedTrainerState;
    }, [selectedTrainerState]);

    // Keep formStateRef updated so save function can access latest formState
    useEffect(() => {
        formStateRef.current = formState;
    }, [formState]);

    // Trigger immediate save when a new trainer name is set (no existing trainer selected)
    // This ensures trainers created via VerifyTrainerModal are saved immediately
    useEffect(() => {
        // Only trigger if:
        // 1. There's a trainer name in formState
        // 2. No trainer is currently selected
        // 3. We're not already saving
        // 4. We haven't already saved this trainer name
        // 5. At least the name field is provided (for new creation, we only need name)
        if (formState.name && 
            !selectedTrainerState && 
            !isSavingRef.current && 
            formState.name !== lastSavedTrainerNameRef.current) {
            // For new entity creation, only require the name field
            // City and state can be added later
            const hasRequiredName = !checkIfFieldIsRequired("name") || formState.name !== "";
            if (hasRequiredName) {
                lastSavedTrainerNameRef.current = formState.name; // Mark as attempting to save
                // Use a small delay to ensure all state updates are processed
                const timeoutId = setTimeout(() => {
                    handleSaveTrainerData();
                }, 150);
                // Cleanup timeout if component unmounts or dependencies change
                return () => clearTimeout(timeoutId);
            }
        }
        // Reset the saved name ref when a trainer is selected (so we can save new names again)
        if (selectedTrainerState) {
            lastSavedTrainerNameRef.current = "";
        }
    }, [formState.name, selectedTrainerState]);

    // Add ref to track if save is in progress to prevent duplicate calls
    const isSavingRef = useRef(false);

    // Register save fn with parent
    useEffect(() => {
        if (!onRegisterSave) return;
        const fn = () => {
            // Use formStateRef to get the latest formState value (avoids stale closure)
            const currentFormState = formStateRef.current;
            if (selectedTrainerRef.current) {
                const currentTrainer = selectedTrainerRef.current;
                const trainerCity = getCityFromTrainer(currentTrainer);
                const trainerState = getStateFromTrainer(currentTrainer);
                const hasChanges =
                    (currentFormState.name && currentFormState.name !== currentTrainer.name) ||
                    currentFormState.city !== trainerCity ||
                    currentFormState.state !== trainerState;
                if (hasChanges) {
                    return handleSaveTrainerData(currentTrainer);
                }
            } else if (currentFormState.name) {
                return handleSaveTrainerData();
            }
        };
        onRegisterSave(fn);
    }, [onRegisterSave, formState, selectedTrainerRef.current]);

    const handleSelectedTrainer = async (trainer?: Trainer, trainerName?: string) => {
        setIsLoading(true);

        let currentTrainer: Trainer | null | undefined = trainer;

        const trainerCity = currentTrainer ? getCityFromTrainer(currentTrainer) : "";
        const trainerState = currentTrainer ? getStateFromTrainer(currentTrainer) : "";

        const currentFormState = {
            name: currentTrainer?.name || trainerName || "",
            city: trainerCity || "",
            state: trainerState || ""
        };

        setSelectedTrainerState(currentTrainer);
        setFormState(currentFormState);
        formStateRef.current = currentFormState; // Update ref immediately so save function can access it

        const isSectionValid = checkIfSectionIsValid(currentFormState);
        // Call the prop function to update the parent state
        if (onSelectTrainer) onSelectTrainer(currentTrainer, isSectionValid);
        
        setIsLoading(false);
    };

    const handleFieldChange = (updatedFormState: TrainerFormState) => {
        if (onFieldChange) {
            const isSectionValid = checkIfSectionIsValid(updatedFormState);
            onFieldChange((selectedTrainerState || undefined), isSectionValid);
        }
    };

    const handleCityChange = (e: any) => {
        const newCity = e.target.value;
        const updatedFormState = {
            name: formState.name,
            city: newCity,
            state: formState.state,
        };
        setFormState((prev) => ({
            ...prev,
            city: e.target.value,
        }));
        handleFieldChange(updatedFormState);
    };

    const handleStateSelection = (selectedState: string) => {
        const updatedFormState = {
            name: formState.name,
            city: formState.city,
            state: (selectedState === "-" ? "" : selectedState),
        };
        setFormState((prev) => ({
            ...prev,
            state: selectedState === "-" ? "" : selectedState,
        }));
        handleFieldChange(updatedFormState);
    };

    const getCityFromTrainer = (o: Trainer) => {
        if (o.address && o.address.city) return o.address.city;
        if (o.location) {
            const trainerAddressArray = o.location.split(",");
            return ((trainerAddressArray[0] && trainerAddressArray[1]) ? trainerAddressArray[0] : ""); 
        }
        return ""
    }

    const getStateFromTrainer = (o: Trainer) => {
        if (o.address && o.address.provState) return o.address.provState;
        if (o.location) {
            const trainerAddressArray = o.location.split(",");
            return ((trainerAddressArray[0] && trainerAddressArray[1]) ? trainerAddressArray[1] : ""); 
        }
        return ""
    }

    const checkIfFieldIsRequired = (fieldName: string) => {
        if (requiredFields && requiredFields.length > 0) {
            const index = requiredFields.findIndex(field => field.toLocaleLowerCase() === fieldName.toLocaleLowerCase());
            if (index > -1) return true;
        }
        return false;
    }

    const checkIfSectionIsValid = (currentFormState: TrainerFormState) => {
        if (checkIfFieldIsRequired("name") && currentFormState.name === "") return false;
        if (checkIfFieldIsRequired("city") && currentFormState.city === "") return false;
        if (checkIfFieldIsRequired("state") && currentFormState.state === "") return false;
        return true;
    }

    const handleSaveTrainerData = async (trainerNeedingUpdate?: Trainer | null | undefined) => {
        // Use formStateRef to get the latest formState value
        const currentFormState = formStateRef.current;
        // If this function is called before any trainer was selected or a name was selected, don't perform any actions.
        if (!trainerNeedingUpdate && !currentFormState.name) return;

        // For new entity creation, only require the name field
        // For updates, require all fields to be valid
        if (trainerNeedingUpdate) {
            // Updating existing trainer - require all fields
            const isSectionValid = checkIfSectionIsValid(currentFormState);
            if (!isSectionValid) {
                return;
            }
        } else {
            // Creating new trainer - only require name
            const hasRequiredName = !checkIfFieldIsRequired("name") || currentFormState.name !== "";
            if (!hasRequiredName) {
                return;
            }
        }

        // Prevent concurrent saves
        if (isSavingRef.current) {
            return;
        }
        isSavingRef.current = true;
        if (onSavingStateChange) onSavingStateChange(true);

        // Track the updatedTrainer
        let updatedTrainer: (Trainer | null | undefined) = trainerNeedingUpdate;

        // Track the current value of Address and Location for this trainer
        let updatedTrainerAddress: (Address | null | undefined) = updatedTrainer?.address;
        if (updatedTrainer?.addressId) {
            const updateInput: UpdateAddressInput = {
                id: updatedTrainer.addressId,
                city: currentFormState.city || "",
                provState: currentFormState.state || ""
            };
            const updateResult = await updateAddress(updateInput);
            if (updateResult.isSuccess) {
                const updatedAddressResult: Address = updateResult.result;
                updatedTrainerAddress = updatedAddressResult;
            }
        } else {
            const createInput: CreateAddressInput = {
                type: "mailing",
                city: currentFormState.city || "",
                provState: currentFormState.state || "",
                notes: "Created during Online Entry process"
            };
            const createResult = await createAddress(createInput);
            if (createResult.isSuccess) {
                const createdAddressResult: Address = createResult.result;
                updatedTrainerAddress = createdAddressResult;
            }
        }
        const currentTrainerLocationValue = updatedTrainerAddress ? formatShortAddress(updatedTrainerAddress) : "";

        // First, check if this trainer already exists.
        if (trainerNeedingUpdate) {
            // Next, check what actually needs to be update in order to avoid an update if the values for the trainer were not changed.
            let shouldUpdateTrainerInDB = false;
            let updateInput: UpdateTrainerInput = {
                id: trainerNeedingUpdate.id,
            };
            if (currentFormState.name && currentFormState.name !== trainerNeedingUpdate.name) {
                shouldUpdateTrainerInDB = true;
                updateInput["name"] = currentFormState.name;
            }
            if (currentTrainerLocationValue !== trainerNeedingUpdate.location) {
                shouldUpdateTrainerInDB = true;
                updateInput["location"] = currentTrainerLocationValue;
            }
            if (updatedTrainerAddress?.id !== trainerNeedingUpdate.addressId) {
                shouldUpdateTrainerInDB = true;
                updateInput["addressId"] = updatedTrainerAddress?.id;
            }
            if (shouldUpdateTrainerInDB) {
                // Run the following command to update the trainer in the DB
                const updateResult = await updateTrainer(updateInput);
                if (updateResult.isSuccess) {
                    updatedTrainer = updateResult.result as Trainer;
                    const formattedOption = await formatSingleTrainer(updatedTrainer);

                    // Find the group that this trainer is in
                    const userGroupIndex = userTrainerOptions?.findIndex((h) => h.value === updatedTrainer?.id);
                    const barnGroupIndex = barnTrainerOptions?.findIndex((h) => h.value === updatedTrainer?.id);

                    // Update the appropriate group
                    if (barnGroupIndex !== undefined && barnGroupIndex > -1 && barnTrainerOptions) {
                        const updatedBarnGroup: formattedOption[] = [
                            ...barnTrainerOptions?.slice(0, barnGroupIndex),
                            formattedOption,
                            ...barnTrainerOptions?.slice(barnGroupIndex + 1),
                        ];
                        setBarnTrainerOptions(updatedBarnGroup);
                    } else if (userGroupIndex !== undefined && userGroupIndex > -1 && userTrainerOptions) {
                        const updatedUserGroup: formattedOption[] = [
                            ...userTrainerOptions?.slice(0, userGroupIndex),
                            formattedOption,
                            ...userTrainerOptions?.slice(userGroupIndex + 1),
                        ];
                        setUserTrainerOptions(updatedUserGroup);
                    } else {
                        // Could not find the trainer in either list
                    }
                } else {
                    // TODO: show error
                }
            }
        }

        // OTW, check if this is a new trainer that needs to be created.
        else if (currentFormState.name) {
            const createInput: CreateTrainerInput = {
                name: currentFormState.name,
                location: currentTrainerLocationValue || "",
                addressId: updatedTrainerAddress?.id || "",
                barnId: barn?.id || null,
                createdBy: user.id
            };
            const createResult = await createTrainer(createInput);
            if (createResult.isSuccess) {
                const newTrainer: Trainer = createResult.result;

                // Set the selectedTrainerRef to now point to the newly created trainer.
                // This prevents a duplicated trainer from being created, as well as allows the user to update the trainer's properties.
                selectedTrainerRef.current = newTrainer;
                updatedTrainer = newTrainer;

                // Format the trainer for the dropdown
                const formattedOption = await formatSingleTrainer(newTrainer, user);

                // Determine which list to add the trainer to based on barn
                const trainerBelongsToCurrentBarn = barn && (newTrainer.barnId === barn.id || createInput.barnId === barn.id);

                // Update state in a way that ensures React detects the change
                if (trainerBelongsToCurrentBarn) {
                    // Trainer belongs to this barn - add to barnTrainerOptions
                    setBarnTrainerOptions((prevBarnTrainers) => {
                        // Check if already in list to prevent duplicates
                        const isAlreadyInList = prevBarnTrainers?.some((h) => h.value === newTrainer.id);
                        if (isAlreadyInList) {
                            return prevBarnTrainers;
                        }
                        // Always create a new array to ensure React detects the change
                        const baseArray = prevBarnTrainers || [];
                        const updatedBarnTrainers: formattedOption[] = [...baseArray, formattedOption];
                        // Sort alphabetically by trainer name
                        const sorted = updatedBarnTrainers.sort((a, b) => a.trainerName.trim().localeCompare(b.trainerName.trim()));
                        // Force a new array reference even after sorting
                        return [...sorted];
                    });

                    // Remove from userTrainerOptions if it exists there (to avoid duplicates)
                    setUserTrainerOptions((prevUserTrainers) => {
                        if (prevUserTrainers && prevUserTrainers.length > 0) {
                            return prevUserTrainers.filter((h) => h.value !== newTrainer.id);
                        }
                        return prevUserTrainers;
                    });
                } else {
                    // Trainer doesn't belong to a barn or belongs to a different barn - add to userTrainerOptions
                    setUserTrainerOptions((prevUserTrainers) => {
                        // Check if already in list to prevent duplicates
                        const isAlreadyInList = prevUserTrainers?.some((h) => h.value === newTrainer.id);
                        if (isAlreadyInList) {
                            return prevUserTrainers;
                        }
                        // Always create a new array to ensure React detects the change
                        const baseArray = prevUserTrainers || [];
                        const updatedUserTrainers: formattedOption[] = [...baseArray, formattedOption];
                        return updatedUserTrainers.sort((a, b) => a.trainerName.trim().localeCompare(b.trainerName.trim()));
                    });

                    // Remove from barnTrainerOptions if it exists there
                    setBarnTrainerOptions((prevBarnTrainers) => {
                        if (prevBarnTrainers && prevBarnTrainers.length > 0) {
                            return prevBarnTrainers.filter((h) => h.value !== newTrainer.id);
                        }
                        return prevBarnTrainers;
                    });
                }

                // Update selectedTrainerState after options are updated to ensure dropdown shows the trainer
                setSelectedTrainerState(newTrainer);
                
                // Also update formState to match the newly created trainer
                const trainerCity = getCityFromTrainer(newTrainer);
                const trainerState = getStateFromTrainer(newTrainer);
                const updatedFormState: TrainerFormState = {
                    name: newTrainer.name || currentFormState.name,
                    city: trainerCity || currentFormState.city,
                    state: trainerState || currentFormState.state,
                };
                setFormState(updatedFormState);
                formStateRef.current = updatedFormState;
                
                // Notify parent component
                const isSectionValid = checkIfSectionIsValid(updatedFormState);
                if (onSelectTrainer) {
                    onSelectTrainer(newTrainer, isSectionValid);
                }
                if (onFieldChange) {
                    onFieldChange(newTrainer, isSectionValid);
                }

                // Reset the saved name ref since we successfully created the trainer
                lastSavedTrainerNameRef.current = "";
            } else {
                // Reset the saved name ref on failure so user can retry
                lastSavedTrainerNameRef.current = "";
            }
        }

        // For updates (not new trainers), we need to update selectedTrainerState
        if (updatedTrainer && trainerNeedingUpdate) {
            setSelectedTrainerState(updatedTrainer);
        }
        isSavingRef.current = false;
        if (onSavingStateChange) onSavingStateChange(false);
    };

    return (
        <>
            <div data-section="trainer">
            <IonRow ref={trainerComponentRef}>
                <IonCol sizeXs="12" sizeMd={formState.name ? "6" : "12"} className={`${(isWindows() || isPlatform("desktop")) ? "" : "pb-0"}`}>
                    <Label>
                        Name
                        {checkIfFieldIsRequired("name") && <RequiredInputIndicator />}
                    </Label>
                    <SelectOrCreateTrainer
                        key={`trainer-select-${barn?.id || "no-barn"}-${barnTrainerOptions?.length || 0}-${userTrainerOptions?.length || 0}`}
                        userTrainerOptions={userTrainerOptions}
                        barnTrainerOptions={barnTrainerOptions}
                        selectedBarn={barn} 
                        selectedValue={selectedTrainerState?.id} 
                        onSelect={(trainer?: Trainer, trainerName?: string) => {
                            handleSelectedTrainer(trainer, trainerName);
                        }}
                    />
                </IonCol>
            
                {formState.name && (
                    // Layout for when name is provided
                    <>
                        <IonCol sizeXs="12" sizeMd="4.5">
                            <IonItem color="white">
                                <IonLabel position="stacked">
                                    City
                                    {checkIfFieldIsRequired("city") && <RequiredInputIndicator />}
                                </IonLabel>
                                <IonInput type="text" value={formState.city} aria-required={true} onIonChange={handleCityChange} />
                            </IonItem>
                        </IonCol>
                        <IonCol sizeXs="12" sizeMd="1.5">
                            <SelectState selectedValue={formState.state} isRequired={checkIfFieldIsRequired("state")} onInputChange={handleStateSelection} className="mb-0" />
                        </IonCol>
                        <MembershipUI
                            entityType="trainer"
                            entityId={selectedTrainerState?.id}
                            disabled={!formState.name}
                            ensureEntityExists={async () => {
                                if (!selectedTrainerState?.id && formState.name && checkIfSectionIsValid(formState)) {
                                    await handleSaveTrainerData(selectedTrainerRef.current);
                                }
                                return selectedTrainerRef.current?.id;
                            }}
                        />
                    </>
                )}

                {isLoading && <FormFieldsInlineSkeleton />}
            </IonRow>
            </div>
        </>
    );
};

export default SelectTrainerSection;
